import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { PerformanceList, PerformanceMetric, InsightCard } from '../../../../components/observatory';
import { apiClient } from '../../../../framework/api/api.client';

interface PerformancePanelProps {
  applicationFilter?: string;
}

export const PerformancePanel: React.FC<PerformancePanelProps> = ({
  applicationFilter,
}) => {
  const [topFunctions, setTopFunctions] = useState<PerformanceMetric[]>([]);
  const [slowestFunctions, setSlowestFunctions] = useState<PerformanceMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');

  useEffect(() => {
    loadMetrics();
    
    const interval = setInterval(() => {
      loadMetrics();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [timeRange, applicationFilter]);

  const loadMetrics = async () => {
    try {
      const params = new URLSearchParams({
        limit: '10',
        timeRange,
        ...(applicationFilter && { application: applicationFilter }),
      });

      const [topRes, slowestRes] = await Promise.all([
        apiClient.call(`/application-observatory/metrics/functions/top?${params}`).catch(() => ({ metrics: [] })),
        apiClient.call(`/application-observatory/metrics/functions/slowest?${params}`).catch(() => ({ metrics: [] })),
      ]);

      // Use mock data if API fails or returns empty
      setTopFunctions(topRes.metrics?.length ? topRes.metrics : generateMockTopFunctions());
      setSlowestFunctions(slowestRes.metrics?.length ? slowestRes.metrics : generateMockSlowestFunctions());
    } catch (error) {
      console.error('Error loading function metrics:', error);
      setTopFunctions(generateMockTopFunctions());
      setSlowestFunctions(generateMockSlowestFunctions());
    } finally {
      setLoading(false);
    }
  };

  const totalCalls = topFunctions.reduce((sum, m) => sum + m.callCount, 0);
  const avgExecutionTime = topFunctions.length > 0
    ? Math.round(topFunctions.reduce((sum, m) => sum + m.averageMs, 0) / topFunctions.length)
    : 0;
  const overallSuccessRate = topFunctions.length > 0
    ? Math.round(topFunctions.reduce((sum, m) => sum + m.successRate, 0) / topFunctions.length)
    : 0;

  return (
    <Box>
      {/* Time Range Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
          >
            <MenuItem value="1h">Last Hour</MenuItem>
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={loadMetrics} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* Top Functions by Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon />
                  <Typography variant="h6">Most Used Functions</Typography>
                </Box>
              }
            />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : (
                <PerformanceList
                  metrics={topFunctions}
                  showRank={true}
                  showProgress={false}
                  emptyMessage="No function calls recorded yet"
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Slowest Functions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon />
                  <Typography variant="h6">Slowest Functions</Typography>
                </Box>
              }
              subheader="Performance optimization candidates"
            />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : (
                <PerformanceList
                  metrics={slowestFunctions}
                  showRank={false}
                  showProgress={true}
                  emptyMessage="No data available"
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Summary */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Performance Summary" />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <SpeedIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {totalCalls.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        TOTAL FUNCTION CALLS
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <TimerIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {avgExecutionTime}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        AVG EXECUTION TIME
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {overallSuccessRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        OVERALL SUCCESS RATE
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <TrendingUpIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {topFunctions.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        UNIQUE FUNCTIONS USED
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Insights */}
        {!loading && (topFunctions.length > 0 || slowestFunctions.length > 0) && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Performance Insights" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Fastest Function */}
                  {slowestFunctions.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <InsightCard
                        icon="⚡"
                        title="Fastest Function"
                        name={`${slowestFunctions[slowestFunctions.length - 1]?.name}()`}
                        value={`${slowestFunctions[slowestFunctions.length - 1]?.averageMs}ms avg`}
                        color="success"
                      />
                    </Grid>
                  )}

                  {/* Most Reliable */}
                  {topFunctions.length > 0 && (() => {
                    const mostReliable = [...topFunctions].sort((a, b) => b.successRate - a.successRate)[0];
                    return (
                      <Grid item xs={12} md={4}>
                        <InsightCard
                          icon="✅"
                          title="Most Reliable"
                          name={`${mostReliable?.name}()`}
                          value={`${mostReliable?.successRate}% success`}
                          sublabel={`${mostReliable?.callCount} calls`}
                          color="primary"
                        />
                      </Grid>
                    );
                  })()}

                  {/* Most Popular */}
                  {topFunctions.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <InsightCard
                        icon="🔥"
                        title="Most Popular"
                        name={`${topFunctions[0]?.name}()`}
                        value={`${topFunctions[0]?.callCount.toLocaleString()} calls`}
                        sublabel={`${topFunctions[0]?.averageMs}ms avg execution`}
                        color="secondary"
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Error Rate by Function */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Error Rate by Function"
              subheader="Functions with highest error rates need attention"
            />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : (
                <Box>
                  {topFunctions
                    .filter(f => f.successRate < 100)
                    .sort((a, b) => a.successRate - b.successRate)
                    .slice(0, 5)
                    .map((func, idx) => (
                      <Box
                        key={func.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          mb: 1,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          border: 1,
                          borderColor: func.successRate < 90 ? 'error.light' : 'divider',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: 'monospace', fontWeight: 600, flex: 1 }}
                        >
                          {func.name}()
                        </Typography>
                        <Box sx={{ width: 200 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Success
                            </Typography>
                            <Typography variant="caption" color={func.successRate < 90 ? 'error.main' : 'text.secondary'}>
                              {func.successRate}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={func.successRate}
                            color={func.successRate < 90 ? 'error' : func.successRate < 95 ? 'warning' : 'success'}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                        <Chip
                          label={`${100 - func.successRate}% errors`}
                          size="small"
                          color={func.successRate < 90 ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  {topFunctions.filter(f => f.successRate < 100).length === 0 && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      All functions have 100% success rate! 🎉
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Mock data generators
function generateMockTopFunctions(): PerformanceMetric[] {
  return [
    { id: '1', name: 'loadMultiLocationDataset', callCount: 4521, averageMs: 11400, minMs: 8500, maxMs: 18000, successRate: 99 },
    { id: '2', name: 'getSalesMetrics', callCount: 3842, averageMs: 245, minMs: 120, maxMs: 890, successRate: 100 },
    { id: '3', name: 'getOrderStatus', callCount: 2156, averageMs: 85, minMs: 45, maxMs: 320, successRate: 100 },
    { id: '4', name: 'createOrder', callCount: 1834, averageMs: 320, minMs: 180, maxMs: 750, successRate: 98 },
    { id: '5', name: 'getStockLevels', callCount: 1245, averageMs: 156, minMs: 80, maxMs: 420, successRate: 100 },
    { id: '6', name: 'updateInventory', callCount: 892, averageMs: 210, minMs: 120, maxMs: 580, successRate: 97 },
    { id: '7', name: 'getEmployeeSchedule', callCount: 654, averageMs: 178, minMs: 90, maxMs: 450, successRate: 100 },
    { id: '8', name: 'processPayment', callCount: 521, averageMs: 890, minMs: 450, maxMs: 2100, successRate: 96 },
  ];
}

function generateMockSlowestFunctions(): PerformanceMetric[] {
  return [
    { id: '1', name: 'loadMultiLocationDataset', callCount: 4521, averageMs: 11400, minMs: 8500, maxMs: 18000, successRate: 99 },
    { id: '2', name: 'generateAnalyticsReport', callCount: 234, averageMs: 5200, minMs: 3200, maxMs: 12000, successRate: 98 },
    { id: '3', name: 'syncInventoryBatch', callCount: 156, averageMs: 3800, minMs: 2100, maxMs: 8500, successRate: 97 },
    { id: '4', name: 'exportSalesData', callCount: 89, averageMs: 2400, minMs: 1200, maxMs: 5600, successRate: 100 },
    { id: '5', name: 'processPayment', callCount: 521, averageMs: 890, minMs: 450, maxMs: 2100, successRate: 96 },
  ];
}

export default PerformancePanel;

